/**
 * Dependency-free HTML sanitizer for the rich-text item description.
 *
 * Parses the input in an inert document, then walks the tree keeping only an
 * allowlist of formatting tags/attributes. Dangerous elements (script, style,
 * iframe, …) are dropped entirely; unknown-but-harmless elements are unwrapped
 * (their text is kept); every attribute except a tiny allowlist is removed (so
 * `on*` handlers and inline `style` can never survive); `javascript:`/`data:`
 * hrefs are stripped. Run it both before saving and before rendering.
 */

// Tag -> allowed attributes.
const ALLOWED: Record<string, readonly string[]> = {
  P: [],
  BR: [],
  B: [],
  STRONG: [],
  I: [],
  EM: [],
  U: [],
  S: [],
  H2: [],
  H3: [],
  UL: [],
  OL: [],
  LI: [],
  BLOCKQUOTE: [],
  A: ['href'],
};

// Elements removed wholesale (content discarded).
const DROP = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE',
  'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'SVG', 'MATH', 'NOSCRIPT',
]);

function cleanChildren(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 8 /* comment */) {
      child.remove();
      continue;
    }
    if (child.nodeType === 3 /* text */) continue;
    if (child.nodeType !== 1 /* element */) {
      child.remove();
      continue;
    }
    const el = child as Element;
    const tag = el.tagName.toUpperCase();

    if (DROP.has(tag)) {
      el.remove();
      continue;
    }

    if (!(tag in ALLOWED)) {
      // Unknown but not dangerous → unwrap: clean its subtree, then hoist its
      // children into place and drop the wrapper.
      cleanChildren(el);
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
      continue;
    }

    // Allowed tag: strip every attribute except its allowlist.
    const allowedAttrs = ALLOWED[tag] ?? [];
    for (const attr of Array.from(el.attributes)) {
      if (!allowedAttrs.includes(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      }
    }
    if (tag === 'A') {
      const href = (el.getAttribute('href') ?? '').trim();
      if (/^(javascript|data|vbscript):/i.test(href) || href === '') {
        el.removeAttribute('href');
      } else {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer nofollow');
      }
    }
    cleanChildren(el);
  }
}

export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  cleanChildren(doc.body);
  return doc.body.innerHTML.trim();
}

/** True when the (sanitized) HTML carries no visible content. */
export function isHtmlEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length === 0;
}
